import React, { useState } from "react";
import moment from "moment";
import { CalendarDays, ChevronRight, IndianRupee, MapPin, Search, X } from "lucide-react";
import Cross from "sushiweb/Icons/all/Cross";
import BackButton from "../../Button/BackButton";
import { BottomModal, Button, FlexColumn } from "../../Common/styles.common";
import { getPurchaseOrderStatusConfig, getPurchaseOrderStatusImage, PURCHASE_ORDER_PAGE_CONFIG } from "./config";
import { buildGoogleMapsUrl } from "utils/mapUtils";
import { formatWeight } from "../PurchaseOrderDetails/PurchaseOrderDetailsElements";
import { Card, CardAction, CardActions, CardHeading, CardHeadingRow, CardTop, Header, HeaderActions, HeaderRow, HeaderTitle, IconButton, MapLink, MetaItem, MetaLabel, MetaPanel, MetaValue, PONumber, POSummary, PageTab, PageTabs, SearchBox, SearchInput, SearchRow, StateButton, StateButtonCount, StateContainer, StateImage, StateMessage, StateTitle, StatusBadge, StatusFilter, StatusFilters, StatusImage } from "./styles";

const formatAmount = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const formatDate = (date) => {
  if (!date || !moment(date).isValid()) {
    return "—";
  }
  return moment(date).format("DD MMM 'YY");
};

export const PurchaseOrderPageTabs = ({
  activePage,
  onChange,
}) => (
  <PageTabs role="tablist" aria-label="Purchase order pages">
    {PURCHASE_ORDER_PAGE_CONFIG.map((page) => (
      <PageTab
        key={page.key}
        type="button"
        role="tab"
        aria-selected={activePage === page.key}
        $active={activePage === page.key}
        onClick={() => onChange(page.key)}
      >
        {page.label}
      </PageTab>
    ))}
  </PageTabs>
);

export const PurchaseOrderListHeader = ({
  activePage,
  searchQuery,
  searchVisible,
  onPageChange,
  onSearchChange,
  onToggleSearch,
}) => (
  <Header>
    <HeaderRow>
      <BackButton pathName="/" variant="header" />
      <HeaderTitle>
        {searchVisible ? "Search purchase orders" : "Purchase orders (POs)"}
      </HeaderTitle>
      <HeaderActions>
        <IconButton
          type="button"
          aria-label={searchVisible ? "Close search" : "Search purchase orders"}
          onClick={onToggleSearch}
        >
          {searchVisible ? <X size={19} /> : <Search size={19} />}
        </IconButton>
      </HeaderActions>
    </HeaderRow>
    {searchVisible ? (
      <SearchRow>
        <SearchBox>
          <Search size={18} />
          <SearchInput
            autoFocus
            type="search"
            value={searchQuery}
            placeholder="e.g. ZHPHR27-PO-2916760"
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </SearchBox>
      </SearchRow>
    ) : (
      <PurchaseOrderPageTabs
        activePage={activePage}
        onChange={onPageChange}
      />
    )}
  </Header>
);

export const PurchaseOrderStatusFilters = ({
  statuses,
  selectedStatus,
  onChange,
}) => {
  if (statuses.length < 2) {
    return null;
  }

  return (
    <StatusFilters aria-label="Filter purchase orders by status">
      {statuses.map((status) => {
        const statusConfig = getPurchaseOrderStatusConfig(status);
        return (
          <StatusFilter
            key={status}
            type="button"
            $active={selectedStatus === status}
            aria-pressed={selectedStatus === status}
            onClick={() => onChange(selectedStatus === status ? "" : status)}
          >
            {statusConfig.label}
          </StatusFilter>
        );
      })}
    </StatusFilters>
  );
};

export const PurchaseOrderStatus = ({ status }) => {
  const [hasImageError, setHasImageError] = useState(false);
  const statusConfig = getPurchaseOrderStatusConfig(status);
  const statusImage = getPurchaseOrderStatusImage(status);

  if (statusImage && !hasImageError) {
    return <StatusImage src={statusImage} alt={statusConfig.label} onError={() => setHasImageError(true)} />;
  }

  return <StatusBadge $color={statusConfig.color}>{statusConfig.label}</StatusBadge>;
};

export const PurchaseOrderCard = ({ item, actions = [], onOpen }) => {
  const po = item;
  const warehouseCity = po.warehouse_details?.city;
  const warehouseName = po.warehouse_code
    ? warehouseCity
      ? `${po.warehouse_code} (${warehouseCity})`
      : po.warehouse_code
    : warehouseCity || po.warehouse_details?.name || "—";
  const mapUrl = buildGoogleMapsUrl(po.warehouse_details?.latitude, po.warehouse_details?.longitude);
  const totalSkus = po.purchase_order_items?.length || 0;
  const totalWeightKg = (po.purchase_order_items || []).reduce(
    (sum, poItem) => sum + (Number(poItem.quantity_ordered || 0) * Number(poItem.weight_per_packet || 0)),
    0
  );
  const weightDisplay = formatWeight(totalWeightKg);
  const metadata = [
    {
      key: "delivery-date",
      icon: CalendarDays,
      label: "Deliver by",
      value: formatDate(po.expected_delivery_date),
    },
    {
      key: "warehouse",
      icon: MapPin,
      label: "Deliver at",
      value: `${po.warehouse_code} (${warehouseCity})`,
      wrap: true,
      mapUrl,
    },
    {
      key: "amount",
      icon: IndianRupee,
      label: "Amount",
      value: formatAmount(po.po_amount),
    },
  ];

  return (
    <Card onClick={onOpen} style={onOpen ? { cursor: "pointer" } : undefined}>
      <CardTop>
        <PurchaseOrderStatus status={(po.po_status === "WAITING_FOR_VENDOR_APPROVAL" || po.po_status === "COMPLETED") ? po.po_status : (po.derived_status || po.po_status)} />
        <CardHeadingRow>
          <CardHeading>
            <PONumber>{po.purchase_order_number}</PONumber>
            <POSummary>
              {totalSkus} SKUs{totalWeightKg > 0 ? ` • ${weightDisplay}` : ""}
            </POSummary>
          </CardHeading>
          <CardActions>
            {actions.map((action) => (
              <CardAction
                key={action.key}
                type="button"
                aria-label={action.label}
                onClick={(e) => { e.stopPropagation(); action.onClick(); }}
                $imageOnly={action.imageOnly}
              >
                {action.icon}
              </CardAction>
            ))}
          </CardActions>
        </CardHeadingRow>
      </CardTop>

      <MetaPanel>
        {metadata.map((metadataItem) => {
          const MetadataIcon = metadataItem.icon;
          return (
            <MetaItem key={metadataItem.key} title={`${metadataItem.label}: ${metadataItem.value}`}>
              <MetadataIcon size={17} />
              <MetaLabel>{metadataItem.label}</MetaLabel>
              <MetaValue $wrap={metadataItem.wrap}>{metadataItem.value}</MetaValue>
              {metadataItem.mapUrl && (
                <MapLink href={metadataItem.mapUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>Open in map</MapLink>
              )}
            </MetaItem>
          );
        })}
      </MetaPanel>
    </Card>
  );
};

export const PurchaseOrderContactModal = ({ contact, onClose, onCall }) => (
  <BottomModal visible={Boolean(contact)} onClose={onClose} showCloseIcon>
    <FlexColumn className="align-items-center p-4">
      <Cross color="#696969" className="close" onClick={onClose} />
      <img src="/logos/po_creator_logo.svg" alt="" />
      <span className="fs-20 fw-700 mt-4">{contact?.name}</span>
      <span className="fs-14 fw-400 text-gray-cool mt-3 mb-4">PO creator</span>
      <Button type="button" onClick={onCall}>
        <span className="fs-13 fw-500 text-white">Call</span>
      </Button>
    </FlexColumn>
  </BottomModal>
);

export const PurchaseOrderSearchPrompt = ({ minimumCharacters, numericOnly }) => (
  <StateContainer>
    <Search size={42} color="#EF4F5F" />
    <StateTitle>Search by PO number</StateTitle>
    <StateMessage>
      {numericOnly
        ? `Enter the complete ${minimumCharacters}-digit ID shown at the end of the PO number, for example 2916760.`
        : `Enter at least ${minimumCharacters} starting characters of the PO number, for example ZHPHR27-PO-2916760. You can also enter its final 7-digit ID.`}
    </StateMessage>
  </StateContainer>
);

export const PurchaseOrderEmptyState = ({
  pageConfig,
  isSearch,
  selectedStatus,
  nextPage,
  nextPageCount,
  onOpenPage,
}) => {
  const selectedStatusConfig = getPurchaseOrderStatusConfig(selectedStatus);
  const isFiltered = Boolean(selectedStatus);
  const title = isSearch
    ? "No matching POs"
    : isFiltered
      ? "No POs here yet"
      : "All caught up!";
  const message = isSearch
    ? "Try a different PO number."
    : isFiltered
      ? selectedStatusConfig.emptyMessage
      : pageConfig.emptyMessage;

  return (
    <StateContainer>
      <StateImage src="/logos/po_empty.svg" alt="" />
      <StateTitle>{title}</StateTitle>
      <StateMessage>{message}</StateMessage>
      {!isSearch && !isFiltered && nextPage && (
        <StateButton type="button" onClick={() => onOpenPage(nextPage.key)}>
          Check {nextPage.label}
          <StateButtonCount>{nextPageCount}</StateButtonCount>
          <ChevronRight size={15} strokeWidth={2.5} />
        </StateButton>
      )}
    </StateContainer>
  );
};

export const PurchaseOrderErrorState = ({ onRetry }) => (
  <StateContainer>
    <StateTitle>Could not load purchase orders</StateTitle>
    <StateMessage>Please check your connection and try again.</StateMessage>
    <StateButton type="button" onClick={onRetry}>
      Retry
    </StateButton>
  </StateContainer>
);
